import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import { formatBytes, filterTree, computeRestorePreviewPath, heartbeatStatus, liveHeartbeat, useIsMobile } from '../utils';

function getAllDescendantPaths(node) {
  const paths = [];
  function walk(n) {
    if (!n || typeof n !== 'object') return;
    if (n.__meta?.path) paths.push(n.__meta.path.replace(/\\/g, '/'));
    for (const [k, v] of Object.entries(n)) {
      if (k !== '__meta') walk(v);
    }
  }
  walk(node);
  return paths;
}

function TreeNode({ name, node, path, selected, onToggle, depth = 0 }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const meta = node.__meta || {};
  const isDir = meta.type === 'dir';
  const children = Object.entries(node).filter(([k]) => k !== '__meta');
  const isSelected = selected.has(meta.path);

  return (
    <div style={{ marginLeft: depth * 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
        cursor: 'pointer', borderRadius: 3,
        background: isSelected ? 'rgba(0,229,160,0.08)' : 'transparent',
      }}
        onMouseEnter={e => e.currentTarget.style.background = isSelected ? 'rgba(0,229,160,0.12)' : 'rgba(255,255,255,0.04)'}
        onMouseLeave={e => e.currentTarget.style.background = isSelected ? 'rgba(0,229,160,0.08)' : 'transparent'}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggle(meta.path, isDir, node)}
          onClick={e => e.stopPropagation()}
          style={{ accentColor: 'var(--green)', cursor: 'pointer', flexShrink: 0 }}
        />
        <span
          onClick={() => isDir && setExpanded(e => !e)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, fontSize: 13, fontFamily: 'var(--mono)' }}
        >
          {isDir ? (
            <span style={{ color: 'var(--accent)', fontSize: 11 }}>{expanded ? '▼' : '▶'}</span>
          ) : (
            <span style={{ width: 11, display: 'inline-block' }} />
          )}
          <span style={{ color: isDir ? 'var(--accent)' : 'var(--text)' }}>
            {isDir ? '📁' : '📄'} {name}
          </span>
          {!isDir && meta.size > 0 && (
            <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 'auto' }}>{formatBytes(meta.size)}</span>
          )}
        </span>
      </div>
      {isDir && expanded && children.map(([k, v]) => (
        <TreeNode key={k} name={k} node={v} path={meta.path + '/' + k}
          selected={selected} onToggle={onToggle} depth={depth + 1} />
      ))}
    </div>
  );
}

function SearchResultRow({ match, selected, onToggle }) {
  const isSelected = selected.has(match.path);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px',
      cursor: 'pointer', borderRadius: 3,
      background: isSelected ? 'rgba(0,229,160,0.08)' : 'transparent',
    }}
      onMouseEnter={e => e.currentTarget.style.background = isSelected ? 'rgba(0,229,160,0.12)' : 'rgba(255,255,255,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = isSelected ? 'rgba(0,229,160,0.08)' : 'transparent'}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => onToggle(match.path, match.isDir, match.node)}
        onClick={e => e.stopPropagation()}
        style={{ accentColor: 'var(--green)', cursor: 'pointer', flexShrink: 0 }}
      />
      <span
        onClick={() => onToggle(match.path, match.isDir, match.node)}
        style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, fontFamily: 'var(--mono)' }}
      >
        <span style={{ fontSize: 13, color: match.isDir ? 'var(--accent)' : 'var(--text)' }}>
          {match.isDir ? '📁' : '📄'} {match.name}
          {!match.isDir && match.size > 0 && (
            <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 8 }}>{formatBytes(match.size)}</span>
          )}
        </span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{match.path}</span>
      </span>
    </div>
  );
}

// #145: sólo linux/macos/windows tienen un target de "ubicación original"
// conocido. Si el servidor no tiene os_type cargado, esa opción se deshabilita
// en vez de adivinar la plataforma.
const KNOWN_OS_TYPES = { linux: '/', macos: '/', windows: 'C:\\' };
const DEFAULT_OTHER_TARGET = { linux: '/tmp/openbackup-restore', macos: '/tmp/openbackup-restore', windows: 'C:\\OpenBackup\\restore' };

// #119 — antes el polling no tenía tope: si el listing quedaba en "pending"
// para siempre (comando perdido en la ventana de reconexión del pub/sub, o
// el agente offline sin que el backend lo detectara), el usuario veía
// "Cargando..." eternamente. 30 intentos * 2s = 60s es más que suficiente
// dado que un listing normal tarda segundos, no minutos.
const MAX_LISTING_POLL_ATTEMPTS = 30;

// #168 — el resultado de la restauración se consulta cada pocos segundos hasta
// ok/error; pasados 10 minutos se deja de consultar (el resultado queda en la
// tabla «Últimas restauraciones» de Restaurar).
export const RESTORE_POLL_MS = 5000;
export const RESTORE_POLL_MAX_MS = 10 * 60 * 1000;

export default function SnapshotExplorer({ run, serverId, server, onClose, defaultTarget, clientServers = [], heartbeatFeed, allowCrossServerRestore = false }) {
  const isMobile = useIsMobile();
  const [status, setStatus]             = useState('loading'); // loading, ready, error, timeout
  // #213 — por qué falló la carga: 'removed' (410, retención: sin reintentar),
  // 'delivery' (no se pudo entregar el comando) o 'agent' (el agente contestó error).
  const [loadError, setLoadError]       = useState(null);      // { kind, text }
  const [retryToken, setRetryToken]     = useState(0);
  const [tree, setTree]                 = useState(null);
  const [selected, setSelected]         = useState(new Set());
  // #158 — destinos posibles para el restore cross-server. Un agente con el WS
  // caído (wsWarning) aparece igual, pero deshabilitado y con el motivo a la
  // vista: un destino que desaparece no comunica nada, y ws_connected (clave
  // ws:online de Redis) puede mentir — la validación real la hace el backend
  // (#119.b), acá sólo avisamos. Si nuestro propio polling está degradado
  // (heartbeatFeed.ok === false) no bloqueamos nada: caemos al last_seen que
  // vino con la lista y habilitamos todo.
  const degraded = heartbeatFeed?.ok === false;
  const otherServerTargets = clientServers
    .filter(s => s.id !== serverId && s.active)
    .map(s => {
      const hb = liveHeartbeat(s, heartbeatFeed);
      const online = degraded
        ? heartbeatStatus(s.last_seen).label === 'online'
        : hb.label === 'online';
      return { server: s, online, disabled: !degraded && hb.wsWarning === true };
    })
    .filter(t => t.online);
  const [targetServerId, setTargetServerId] = useState(serverId);
  const isCrossServerTarget = targetServerId !== serverId;
  const targetServer = isCrossServerTarget ? clientServers.find(s => s.id === targetServerId) : null;
  const osType             = server?.os_type;
  const canUseOriginal     = !isCrossServerTarget && osType in KNOWN_OS_TYPES;
  const originalTarget     = KNOWN_OS_TYPES[osType];
  const [restoreMode, setRestoreMode]   = useState('other'); // 'other' | 'original'
  const [target, setTarget]             = useState(() => DEFAULT_OTHER_TARGET[osType] || DEFAULT_OTHER_TARGET.linux);
  const [hostnameConfirm, setHostnameConfirm] = useState('');
  const [restoring, setRestoring]       = useState(false);
  const [done, setDone]                 = useState(false);
  const [restoreId, setRestoreId]       = useState(null);
  const [restoreResult, setRestoreResult] = useState({ state: 'running', message: '' }); // running | ok | error | timeout
  const [validationError, setValidationError] = useState('');
  const [restoreError, setRestoreError] = useState('');   // ← reemplaza alert()
  const [query, setQuery]               = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    if (isCrossServerTarget && restoreMode === 'original') setRestoreMode('other');
  }, [isCrossServerTarget, restoreMode]);

  useEffect(() => {
    if (!run?.snapshot_id) { setLoadError(null); setStatus('error'); return; }
    let interval;
    let attempts = 0;
    const start = async () => {
      setStatus('loading');
      setLoadError(null);
      try {
        const res = await api.listSnapshot(serverId, run.snapshot_id);
        const listingId = res.listing_id;
        if (res.status === 'error') {
          // #119 — el backend ya sabe que no se pudo entregar el comando
          // (agente offline): no hace falta ni empezar a pollear.
          setLoadError({ kind: 'delivery', text: res.detail || 'No se pudo entregar el comando al agente.' });
          setStatus('error');
          return;
        }
        interval = setInterval(async () => {
          attempts += 1;
          try {
            const r = await api.getListing(listingId);
            if (r.status === 'ready') {
              clearInterval(interval);
              setTree(r.tree);
              setStatus('ready');
            } else if (r.status === 'error') {
              clearInterval(interval);
              setLoadError({ kind: 'agent', text: r.error_msg || '' });
              setStatus('error');
            } else if (attempts >= MAX_LISTING_POLL_ATTEMPTS) {
              clearInterval(interval);
              setStatus('timeout');
            }
          } catch (e) { clearInterval(interval); setLoadError({ kind: 'delivery', text: e.message || '' }); setStatus('error'); }
        }, 2000);
      } catch (e) {
        // 410: el punto de restauración lo borró la retención; el detail del backend lo explica
        setLoadError(e.status === 410
          ? { kind: 'removed', text: e.message }
          : { kind: 'delivery', text: e.message || '' });
        setStatus('error');
      }
    };
    start();
    return () => clearInterval(interval);
  }, [run, serverId, retryToken]);

  useEffect(() => {
    if (!restoreId) return undefined;
    const startedAt = Date.now();
    const interval = setInterval(async () => {
      try {
        const r = await api.getRestore(restoreId);
        if (r.status === 'ok') {
          clearInterval(interval);
          setRestoreResult({ state: 'ok', message: '' });
          return;
        }
        if (r.status === 'error' || r.status === 'rejected' || r.status === 'cancelled') {
          clearInterval(interval);
          setRestoreResult({ state: 'error', message: r.error_msg || '' });
          return;
        }
      } catch {
        // un fallo de red puntual no corta el seguimiento: manda el tope de tiempo
      }
      if (Date.now() - startedAt >= RESTORE_POLL_MAX_MS) {
        clearInterval(interval);
        setRestoreResult({ state: 'timeout', message: '' });
      }
    }, RESTORE_POLL_MS);
    return () => clearInterval(interval);
  }, [restoreId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { matches, truncated } = useMemo(() => filterTree(tree, debouncedQuery), [tree, debouncedQuery]);

  const normPath = (p) => p ? p.replace(/\\/g, '/') : p;

  const togglePath = useCallback((path, isDir, node) => {
    const p = normPath(path);
    setSelected(prev => {
      const next = new Set(prev);
      const descendants = getAllDescendantPaths(node).map(x => normPath(x));
      if (next.has(p)) {
        // Deselect this node and all its descendants
        next.delete(p);
        for (const d of descendants) next.delete(d);
      } else {
        // Select this node and all its descendants
        next.add(p);
        for (const d of descendants) next.add(d);
      }
      return next;
    });
  }, [normPath]);

  const canConfirmHostname = !!server?.hostname && hostnameConfirm === server.hostname;
  const effectiveTarget = restoreMode === 'original' ? originalTarget : target;

  const firstSelectedPath = useMemo(() => {
    if (selected.size === 0) return null;
    return Array.from(selected).sort()[0];
  }, [selected]);

  const previewPath = restoreMode === 'other'
    ? computeRestorePreviewPath(firstSelectedPath, target)
    : null;

  const handleRestore = async () => {
    if (selected.size === 0) { setValidationError('Seleccioná al menos un archivo o carpeta'); return; }
    if (restoreMode === 'original' && !canConfirmHostname) {
      setValidationError('Escribí el hostname del servidor para confirmar la restauración en la ubicación original');
      return;
    }
    setValidationError('');
    setRestoreError('');
    setRestoring(true);
    try {
      const res = await api.granularRestore(serverId, run.snapshot_id, effectiveTarget, Array.from(selected), isCrossServerTarget ? targetServerId : null);
      setRestoreResult({ state: 'running', message: '' });
      setRestoreId(res?.restore_id || null);
      setDone(true);
    } catch (e) {
      setRestoreError(e.message || 'Error al encolar la restauración');
    } finally {
      setRestoring(false);
    }
  };

  const rootEntries = tree ? Object.entries(tree).filter(([k]) => k !== '__meta') : [];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        width: isMobile ? '95vw' : 700, maxHeight: isMobile ? '90vh' : '85vh', display: 'flex', flexDirection: 'column',
        clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))'
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 14 }}>🔄 Restaurar snapshot</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
              {run?.job_name} — {run?.snapshot_id} — {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>

        {/* Buscador */}
        {status === 'ready' && !done && (
          <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)' }}>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar por nombre de archivo o carpeta..."
              style={{
                background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
                padding: '6px 10px', fontSize: 12, fontFamily: 'var(--mono)', width: '100%', boxSizing: 'border-box'
              }}
            />
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 8px' }}>
          {status === 'loading' && (
            <div style={{ textAlign: 'center', padding: 60, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 13 }}>
              ⟳ Cargando contenido del snapshot...
            </div>
          )}
          {status === 'error' && (
            <div style={{ textAlign: 'center', padding: 60, color: '#ff4444', fontFamily: 'var(--mono)', fontSize: 13 }}>
              {loadError?.kind === 'removed' && <>✕ {loadError.text}</>}
              {loadError?.kind === 'delivery' && <>✕ {loadError.text || 'No se pudo entregar el comando al agente.'}</>}
              {loadError?.kind === 'agent' && (
                <>
                  ✕ El agente no pudo leer este punto de restauración.
                  {loadError.text && <div style={{ marginTop: 8, fontSize: 11, color: 'var(--muted)' }}>{loadError.text}</div>}
                </>
              )}
              {!loadError && <>✕ No se pudo cargar el snapshot.</>}
              {loadError?.kind !== 'removed' && (
                <div style={{ marginTop: 16 }}>
                  <button onClick={() => setRetryToken(t => t + 1)} style={{
                    background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)',
                    padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer',
                  }}>REINTENTAR</button>
                </div>
              )}
            </div>
          )}
          {status === 'timeout' && (
            <div style={{ textAlign: 'center', padding: 60, color: '#ff4444', fontFamily: 'var(--mono)', fontSize: 13 }}>
              ✕ El agente no respondió a tiempo.
              <div style={{ marginTop: 16 }}>
                <button onClick={() => setRetryToken(t => t + 1)} style={{
                  background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)',
                  padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer',
                }}>REINTENTAR</button>
              </div>
            </div>
          )}
          {status === 'ready' && done && (
            <div style={{
              textAlign: 'center', padding: 60, fontFamily: 'var(--mono)', fontSize: 13,
              color: restoreResult.state === 'error' ? '#ff4444' : restoreResult.state === 'ok' ? 'var(--green)' : 'var(--text)',
            }}>
              {restoreResult.state === 'running' && <>⟳ Restauración en curso...<br /></>}
              {restoreResult.state === 'ok' && <>✓ Restaurado.<br /></>}
              {restoreResult.state === 'error' && <>✕ La restauración falló.{restoreResult.message && <><br />{restoreResult.message}</>}<br /></>}
              {restoreResult.state === 'timeout' && <>La restauración sigue en curso; el resultado va a aparecer en Restaurar.<br /></>}
              <span style={{ fontSize: 11, color: 'var(--muted)' }}>
                {restoreResult.state === 'ok' ? 'Los archivos se restauraron en' : 'Destino:'} {effectiveTarget}
              </span>
            </div>
          )}
          {status === 'ready' && !done && debouncedQuery.trim() !== '' && (
            truncated ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                Más de 200 coincidencias — afiná la búsqueda para ver resultados.
              </div>
            ) : matches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                Sin coincidencias para "{debouncedQuery}"
              </div>
            ) : (
              <>
                <div style={{ padding: '4px 8px 8px', fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                  {matches.length} coincidencia{matches.length !== 1 ? 's' : ''}
                </div>
                {matches.map(m => (
                  <SearchResultRow key={m.path} match={m} selected={selected} onToggle={togglePath} />
                ))}
              </>
            )
          )}
          {status === 'ready' && !done && debouncedQuery.trim() === '' && rootEntries.map(([k, v]) => (
            <TreeNode key={k} name={k} node={v} path={'/' + k}
              selected={selected} onToggle={togglePath} depth={0} />
          ))}
        </div>

        {/* Footer */}
        {status === 'ready' && !done && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
            {/* Error de restore inline — reemplaza alert() */}
            {restoreError && (
              <div style={{
                marginBottom: 10, padding: '8px 12px',
                background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)',
                color: 'var(--red)', fontSize: 11, fontFamily: 'var(--mono)'
              }}>
                ✕ {restoreError}
              </div>
            )}
            {allowCrossServerRestore && otherServerTargets.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, fontFamily: 'var(--mono)' }}>SERVIDOR DESTINO</div>
                <select
                  value={targetServerId}
                  onChange={e => setTargetServerId(e.target.value)}
                  style={{ width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 10px', fontSize: 12, fontFamily: 'var(--mono)', boxSizing: 'border-box' }}
                >
                  <option value={serverId}>Este mismo servidor ({server?.hostname})</option>
                  {otherServerTargets.map(({ server: s, disabled }) => (
                    <option key={s.id} value={s.id} disabled={disabled}>
                      {disabled
                        ? `${s.hostname} — WS desconectado (no puede recibir el comando de restore)`
                        : s.hostname}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!allowCrossServerRestore && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 14, fontStyle: 'italic' }}>
                💡 Se puede habilitar restaurar este backup en otro servidor del mismo cliente desde la ficha del cliente.
              </div>
            )}
            {isCrossServerTarget && targetServer && (
              <div style={{
                background: 'rgba(255,77,109,0.1)', border: '2px solid var(--red)', padding: '10px 14px',
                fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 14, color: 'var(--red)', fontWeight: 700,
              }}>
                ⚠ RESTORE CRUZADO: este snapshot es de <u>{server?.hostname}</u> y se va a escribir en <u>{targetServer.hostname}</u>, un servidor distinto.
              </div>
            )}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                <input
                  type="radio"
                  checked={restoreMode === 'other'}
                  onChange={() => setRestoreMode('other')}
                  style={{ marginTop: 3, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>Restaurar en otra carpeta</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: canUseOriginal ? 'pointer' : 'not-allowed', opacity: canUseOriginal ? 1 : 0.5 }}>
                <input
                  type="radio"
                  checked={restoreMode === 'original'}
                  onChange={() => canUseOriginal && setRestoreMode('original')}
                  disabled={!canUseOriginal}
                  style={{ marginTop: 3, cursor: canUseOriginal ? 'pointer' : 'not-allowed' }}
                />
                <span style={{ fontSize: 12, fontFamily: 'var(--mono)' }}>
                  Restaurar en la ubicación original
                  {!canUseOriginal && (
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                      No se pudo determinar el sistema operativo de este servidor — usá "otra carpeta".
                    </span>
                  )}
                </span>
              </label>
            </div>

            {restoreMode === 'other' ? (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4, fontFamily: 'var(--mono)' }}>DESTINO</div>
                <input
                  value={target}
                  onChange={e => setTarget(e.target.value)}
                  style={{
                    background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)',
                    padding: '6px 10px', fontSize: 12, fontFamily: 'var(--mono)', width: '100%', boxSizing: 'border-box'
                  }}
                />
                {previewPath && (
                  <div style={{ marginTop: 6, fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--muted)', wordBreak: 'break-all' }}>
                    {firstSelectedPath} <span style={{ color: 'var(--accent)' }}>→</span> {previewPath}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px',
                  fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 12, color: 'var(--red)'
                }}>
                  ⚠ Esta opción SOBRESCRIBE los archivos que hoy existen en {originalTarget} del servidor. Si el
                  archivo actual es más nuevo que el del backup, se pierde sin posibilidad de recuperarlo.
                </div>
                <div style={{ fontSize: 12, marginBottom: 10 }}>
                  Destino: <strong style={{ fontFamily: 'var(--mono)' }}>{originalTarget}</strong>
                </div>
                <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
                  Escribí el hostname exacto para confirmar
                </label>
                <input
                  type="text"
                  value={hostnameConfirm}
                  onChange={e => setHostnameConfirm(e.target.value)}
                  placeholder={server?.hostname}
                  style={{
                    width: '100%', background: 'var(--bg)', border: `1px solid ${canConfirmHostname ? 'var(--red)' : 'var(--border)'}`,
                    color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleRestore}
                disabled={restoring || selected.size === 0 || (restoreMode === 'original' && !canConfirmHostname)}
                style={{
                  background: 'var(--green)', color: '#000', border: 'none',
                  padding: '8px 20px', fontFamily: 'var(--mono)', fontSize: 12, cursor: 'pointer',
                  opacity: (restoring || selected.size === 0 || (restoreMode === 'original' && !canConfirmHostname)) ? 0.5 : 1
                }}
              >
                {restoring ? 'ENCOLANDO...' : `RESTAURAR (${selected.size})`}
              </button>
            </div>
            {validationError && (
              <div style={{ color: 'var(--red)', fontSize: 11, fontFamily: 'var(--mono)', marginTop: 6 }}>
                {validationError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
